/**
 * El detalle de lo que sacaron y pusieron los socios, y los retiros viejos que
 * todavia no tienen dueño.
 */
import { useState } from "react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { Segmented } from "@/components/ui/segmented";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { formatearFecha } from "@/lib/utils";
import { aNumero, type ClaseMovimientoSocio, type MovimientoSocio, type Socio } from "@/lib/tipos";

import { ETIQUETA_CLASE } from "./logica";

const VACIO = "No hay movimientos de socios en este período.";

/** De donde salio o adonde entro: la caja, o el producto que se llevo. */
function detalleDe(m: MovimientoSocio): string {
  return [m.detalle, m.cuenta].filter(Boolean).join(" · ") || "—";
}

/**
 * El detalle es para revisar lo cargado, no para comparar: cada fila va en la
 * moneda en que se movio. La comparacion en dolares ya esta arriba.
 *
 * El filtro de socio lo maneja la pantalla y no este componente: el boton de
 * carga lo lee para abrir el dialogo con ese socio ya elegido.
 */
export function DetalleSocios({
  socios,
  movimientos,
  socio,
  onSocioChange,
}: {
  socios: ReadonlyArray<Socio>;
  movimientos: ReadonlyArray<MovimientoSocio>;
  /** "todos" o el id del socio como texto. */
  socio: string;
  onSocioChange: (socio: string) => void;
}): JSX.Element {
  const [clase, setClase] = useState<"todas" | ClaseMovimientoSocio>("todas");

  const filas = movimientos.filter(
    (m) =>
      m.socio_id !== null &&
      (socio === "todos" || String(m.socio_id) === socio) &&
      (clase === "todas" || m.clase === clase),
  );

  return (
    <section aria-label="Movimientos de los socios" className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Chips con scroll propio en el celular: la pagina nunca scrollea de costado (§8). */}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
        <div className="sin-scrollbar overflow-x-auto">
          <Segmented
            etiqueta="Socio"
            apariencia="chips"
            tamano="sm"
            opciones={[
              { valor: "todos", etiqueta: "Los dos" },
              ...socios.map((s) => ({ valor: String(s.id), etiqueta: s.nombre })),
            ]}
            valor={socio}
            onChange={onSocioChange}
          />
        </div>
        <div className="sin-scrollbar overflow-x-auto">
          <Segmented
            etiqueta="Tipo de movimiento"
            apariencia="chips"
            tamano="sm"
            opciones={[
              { valor: "todas" as const, etiqueta: "Todo" },
              ...(Object.keys(ETIQUETA_CLASE) as ClaseMovimientoSocio[]).map((c) => ({
                valor: c,
                etiqueta: ETIQUETA_CLASE[c],
              })),
            ]}
            valor={clase}
            onChange={setClase}
          />
        </div>
      </div>

      {/* Desktop: tabla densa, con el scroll adentro. */}
      <Card padding="none" className="hidden min-h-0 flex-1 flex-col overflow-hidden md:flex">
        <Table densidad="compacta" alto="lleno">
          <TableHeader>
            <TableRow>
              <TableHead>Día</TableHead>
              <TableHead>Socio</TableHead>
              <TableHead>Movimiento</TableHead>
              <TableHead>Detalle</TableHead>
              <TableHead className="text-right">Importe</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filas.length === 0 ? (
              <TableVacia colSpan={5} mensaje={VACIO} />
            ) : (
              filas.map((m) => (
                <TableRow key={m.clave}>
                  <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                    {formatearFecha(m.fecha)}
                  </TableCell>
                  <TableCell className="text-sm">{m.socio}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{ETIQUETA_CLASE[m.clase]}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                    {detalleDe(m)}
                  </TableCell>
                  <TableCellImporte monto={aNumero(m.importe)} moneda={m.moneda} />
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Mobile: una tarjeta-registro por movimiento, con la barra de su moneda. */}
      <div className="flex flex-col gap-2 md:hidden">
        {filas.length === 0 ? (
          <EstadoVacio titulo={VACIO} tamano="sm" />
        ) : (
          filas.map((m) => (
            <Card
              key={m.clave}
              as="article"
              padding="compacto"
              acento={m.moneda}
              className="flex items-start justify-between gap-3"
            >
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium">
                  {ETIQUETA_CLASE[m.clase]} · {m.socio}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {formatearFecha(m.fecha)} · {detalleDe(m)}
                </p>
              </div>
              <div className="shrink-0">
                <Importe monto={aNumero(m.importe)} moneda={m.moneda} tamano="sm" />
              </div>
            </Card>
          ))
        )}
      </div>
    </section>
  );
}

/**
 * Los retiros y aportes cargados antes de que existieran los socios.
 *
 * Se asignan de a uno y una sola vez: el backend no deja cambiar el socio de
 * uno que ya lo tiene.
 */
export function SinAsignar({
  socios,
  movimientos,
  asignando,
  onAsignar,
}: {
  socios: ReadonlyArray<Socio>;
  movimientos: ReadonlyArray<MovimientoSocio>;
  asignando: boolean;
  onAsignar: (movimientoId: number, socioId: number) => void;
}): JSX.Element | null {
  if (movimientos.length === 0) return null;
  return (
    <Aviso
      tono="warning"
      titulo={`${movimientos.length} ${movimientos.length === 1 ? "retiro o aporte" : "retiros o aportes"} sin socio`}
    >
      <p className="mb-2">
        Se cargaron antes de que la caja preguntara de quién era la plata. No suman a nadie hasta
        que les digas de quién son.
      </p>
      <ul className="divide-y divide-border">
        {movimientos.map((m) => (
          <li key={m.clave} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <span>
              {formatearFecha(m.fecha)} · {ETIQUETA_CLASE[m.clase]} ·{" "}
              <Importe monto={aNumero(m.importe)} moneda={m.moneda} tamano="sm" />
              {m.detalle ? ` · ${m.detalle}` : ""}
            </span>
            <span className="flex gap-2">
              {socios.map((s) => (
                <Button
                  key={s.id}
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={asignando}
                  onClick={() => onAsignar(Number(m.origen_id), s.id)}
                >
                  Es de {s.nombre}
                </Button>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </Aviso>
  );
}
